import { useState, useMemo, useRef, useEffect } from "react";
import { useApiCall } from '../../../component/custom-hooks/api-call';
import DataNotFound from "../../../component/ui/loader/data-not-found";
import InnerLoader from "../../../component/ui/loader/inner-loader";
import { MetricCard } from "../../../component/new-ui/cards";
import { ArrowDownCircle, ArrowUpCircle, Calendar, Scale, Wallet } from "lucide-react";
import { pkr, usd } from "../../../component/custom-hooks/capitalizeWords";
import { FileUploadProgressBar } from "../../../component/form/form-layout";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

// Simulated pending suggestions from Bills (bills marked paid)
// const INITIAL_PENDING = [
//   { id: "p1", expense: "Rent — Ittehad", vendor: "Landlord", cat: "Rent", loc: "Ittehad", amt: 329450, method: "Cheque", ref: "88242401", date: "2026-03-05", month: "March" },
//   { id: "p2", expense: "Rent — Phase 8", vendor: "Landlord", cat: "Rent", loc: "Phase 8", amt: 65000, method: "Cheque", ref: "88242401", date: "2026-03-05", month: "March" },
//   { id: "p3", expense: "Security — Ittehad", vendor: "Security Co.", cat: "Security", loc: "Ittehad", amt: 32500, method: "Cheque", ref: "88242402", date: "2026-03-05", month: "March" },
// ];

const dateLabel = d => { const dt = new Date(d); return dt.toLocaleDateString("en-PK", { day: "numeric", month: "short", year: "numeric" }); };

const BLANK_EXPENSE = {
  type: "expense",
  desc: "",
  date: new Date().toISOString().slice(0, 10),
  amt: "",
  cat: 0,
  loc: 0,
  method: "",
  ref: "",
  chequeGroup: "",
  month: "",
  year: "",
  attachments: "",
  from_petty_cash: false
};

const BLANK_REMIT = {
  type: "remittance",
  date: new Date().toISOString().slice(0, 10),
  desc: "",
  cat: "",
  loc: "",
  usd: "",
  fx: 0,
  wht: "",
  month: "",
  ref: "",
  attachments: "",
};

const BLANK_DRAW = {
  type: "draw",
  date: new Date().toISOString().slice(0, 10),
  cheque: "",
  amount: "",
  notes: "",
  attachments: ""
};

const BLANK_RETURN = {
  type: "return",
  date: new Date().toISOString().slice(0, 10),
  amount: "",
  notes: "",
  attachments: "",
};

// ─── ADD ENTRY MODAL ──────────────────────────────────────────────────────────
function AddModal({ initial, onSave, onClose, C, categories, locations, dropdowns, fxRate, whtRate }) {
  const [form, setForm] = useState(initial || BLANK_EXPENSE);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const pkrGross = form.type === "remittance" ? (parseFloat(form.usd) || 0) * (parseFloat(form.fx) || 0) : 0;
  const whtAmt = form.type === "remittance" ? (form.wht !== "" ? parseFloat(whtRate) : pkrGross * (whtRate/100)) : 0;
  const pkrNet = pkrGross - whtAmt;
  const lbl = { fontSize: 11, fontWeight: 600, color: "#9ca3af", letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 5, display: "block" };
  const inp = (x = {}) => ({ padding: "8px 11px", borderRadius: 8, border: "1px solid rgba(0,0,0,0.08)", background: "#f9f8f7", fontSize: 12, color: "#111827", outline: "none", width: "100%", boxSizing: "border-box", ...x });
  const sel = (x = {}) => ({ ...inp(), cursor: "pointer", ...x });

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }} onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: 18, boxShadow: "0 8px 48px rgba(0,0,0,0.2)", width: "100%", maxWidth: 520, maxHeight: "92vh", overflow: "auto" }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: "20px 24px", borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: "#111827", }}>
            {initial ? "Confirm Entry" : "Add Transaction"}
          </h2>
          {initial && <p style={{ margin: "4px 0 0", fontSize: 12, color: "#d97706" }}>⚡ Suggested from Bills — confirm to add to Tracker</p>}
        </div>
        <div style={{ padding: "22px 24px" }}>
          {/* Type toggle */}
          {!initial && (
            <div style={{ display: "flex", gap: 0, borderRadius: 10, border: "1px solid rgba(0,0,0,0.08)", overflow: "hidden", marginBottom: 18 }}>
              {[{ v: "expense", label: "📤 Expense" }, { v: "remittance", label: "💵 Remittance" }].map(({ v, label }) => (
                <button key={v} onClick={() => setForm(v === "remittance" ? { ...BLANK_REMIT, fx: fxRate } : { ...BLANK_EXPENSE })}
                  style={{
                    flex: 1, padding: "10px", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600,
                    background: form.type === v ? (v === "remittance" ? "#f0fdf4" : "#fef2f2") : "#f9fafb",
                    color: form.type === v ? (v === "remittance" ? "#16a34a" : "#dc2626") : "#6b7280", transition: "all 0.15s"
                  }}>
                  {label}
                </button>
              ))}
            </div>
          )}

          {/* REMITTANCE FIELDS */}
          {form.type === "remittance" && (
            <div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={lbl}>Date</label>
                  <input type="date" value={form.date} onChange={e => set("date", e.target.value)} style={inp()} />
                </div>
                <div>
                  <label style={lbl}>Reference</label>
                  <input value={form.ref} onChange={e => set("ref", e.target.value)} placeholder="e.g. REM-MAR-01" style={inp()} />
                </div>
                <div>
                  <label style={lbl}>USD Received</label>
                  <input type="number" value={form.usd} onChange={e => set("usd", e.target.value)} placeholder="e.g. 15000" style={inp()} />
                </div>
                <div>
                  <label style={lbl}>FX Rate (₨ per $1)</label>
                  <input type="number" value={form.fx} onChange={e => set("fx", e.target.value)} placeholder={fxRate} style={inp()} />
                </div>
              </div>
              {/* Calculated preview */}
              {pkrGross > 0 && (
                <div style={{ background: "#f0fdf4", borderRadius: 10, border: "1px solid #bbf7d0", padding: "14px 16px", marginBottom: 12 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#166534", letterSpacing: 0.7, textTransform: "uppercase", marginBottom: 10 }}>Calculation Preview</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                    {[
                      { label: "PKR Gross", val: pkr(pkrGross) },
                      { label: `WHT (${whtRate})`, val: pkr(whtAmt) },
                      { label: "PKR Net", val: pkr(pkrNet), bold: true },
                    ].map(r => (
                      <div key={r.label} style={{ background: "#fff", borderRadius: 8, padding: "8px 10px", border: "1px solid #bbf7d0" }}>
                        <div style={{ fontSize: 9, color: "#166534", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 3 }}>{r.label}</div>
                        <div style={{ fontSize: 13, fontWeight: r.bold ? 800 : 600, color: "#16a34a", }}>{r.val}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <label style={lbl}>Override WHT (optional)</label>
                    <input type="number" value={form.wht} onChange={e => set("wht", e.target.value)} placeholder={`Auto: ${pkr(pkrGross * (whtRate / 100), false)}`} style={inp({ width: "50%" })} />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* EXPENSE FIELDS */}
          {form.type === "expense" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
              <div style={{ gridColumn: "1/-1" }}>
                <label style={lbl}>Description</label>
                <input value={form.desc} onChange={e => set("desc", e.target.value)} placeholder="e.g. Rent — Ittehad" style={inp()} />
              </div>
              <div>
                <label style={lbl}>Date</label>
                <input type="date" value={form.date} onChange={e => set("date", e.target.value)} style={inp()} />
              </div>
              <div>
                <label style={lbl}>Amount (PKR)</label>
                <input type="number" value={form.amt} onChange={e => set("amt", e.target.value)} placeholder="e.g. 329450" style={inp()} />
              </div>
              <div>
                <label style={lbl}>Category</label>
                <select value={form.cat} onChange={e => set("cat", parseInt(e.target.value))} style={sel()}>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>Office / Location</label>
                <select value={form.loc} onChange={e => set("loc", e.target.value)} style={sel()}>
                  <option value="">All / General</option>
                  {locations.map(l => <option key={l.id} value={l?.id}>{l.name}</option>)}
                </select>
              </div>

              <div className="col-span-2">
                <label
                  className="bg-[#eff6ff] rounded-xl border border-[#bfdbfe] py-3 px-3.5 mb-2 flex"
                  htmlFor="pettyCashFund"
                >
                  <div className="flex gap-3">
                    <div className="pt-[3px]">
                      <input
                        type="checkbox"
                        id="pettyCashFund"
                        onChange={(e) => {
                          if (e.target.checked) {
                            set("from_petty_cash", true);
                          } else {
                            set("from_petty_cash", false);
                          }
                        }}
                      />
                    </div>

                    <div>
                      <div className="text-sm font-bold text-[#2563eb]">
                        Pay from Petty Cash Fund
                      </div>
                      <div className="text-xs font-medium text-[#1d4ed8] tracking-wide">
                        Deducts from petty in hand, not the bank
                      </div>
                    </div>
                  </div>
                </label>
              </div>

              {!form.from_petty_cash && (
                <div>
                  <label style={lbl}>Payment Method</label>
                  <select
                    value={form.method}
                    onChange={(e) => set("method", e.target.value)}
                    style={sel()}
                  >
                    <option value="">- Select -</option>
                    {dropdowns.method.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className={form.from_petty_cash ? "col-span-2" : ""}>
                <label style={lbl}>Cheque # / Reference</label>
                <input
                  value={form.ref}
                  onChange={(e) => set("ref", e.target.value)}
                  placeholder="e.g. 88242401"
                  style={inp()}
                />
              </div>


              {form.method === "Cheque" && (
                <div className="col-span-2">
                  <label style={lbl}>Combined Cheque Group (optional)</label>
                  <input value={form.chequeGroup} onChange={e => set("chequeGroup", e.target.value)} placeholder="Same # as other vendors on this cheque" style={inp()} />
                </div>
              )}

              <div>
                <label style={lbl}>Month</label>
                <select
                  value={form.month}
                  onChange={e => set("month", e.target.value)}
                  style={sel()}
                >
                  <option value="">Select Month</option>
                  {MONTHS.map((m, i) => (
                    <option key={i} value={m}>{m}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={lbl}>Year</label>
                <input
                  type="number"
                  value={form.year}
                  onChange={e => set("year", e.target.value)}
                  placeholder="e.g. 2026"
                  min="2000"
                  max="2100"
                  style={inp()}
                />
              </div>
            </div>
          )}

          {/* Attachment */}
          <div style={{ marginBottom: 20 }}>
            <label style={lbl}>Receipt / Invoice</label>
            <FileUploadProgressBar onChange={e => set("attachments", e)} />
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, paddingTop: 14, borderTop: "1px solid rgba(0,0,0,0.08)" }}>
            <button onClick={onClose} style={{ padding: "9px 20px", borderRadius: 9, border: "1px solid rgba(0,0,0,0.08)", background: "#f9f8f7", fontSize: 13, fontWeight: 600, color: "#6b7280", cursor: "pointer" }}>Cancel</button>
            <button onClick={() => onSave({ ...form, whtCalc: whtAmt, pkrNet })}
              style={{ padding: "9px 24px", borderRadius: 9, border: "none", background: form.type === "remittance" ? "#16a34a" : "#4f6ef7", fontSize: 13, fontWeight: 700, color: "#fff", cursor: "pointer", boxShadow: `0 2px 8px ${form.type === "remittance" ? "rgba(22,163,74,0.3)" : "rgba(79,110,247,0.3)"}` }}>
              {initial ? "✓ Add to Tracker" : "Add Entry"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ADD PETTY CASH
function AddPettyCashModal({ initial, onSave, onClose, pettyCashBalance }) {
  const [form, setForm] = useState(initial || BLANK_DRAW);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const pkrGross = form.type === "draw" ? (parseFloat(form.usd) || 0) * (parseFloat(form.fx) || 0) : 0;
  const whtAmt = form.type === "draw" ? (form.wht !== "" ? parseFloat(form.wht) : pkrGross * 0.0025) : 0;
  const pkrNet = pkrGross - whtAmt;

  const lbl = { fontSize: 11, fontWeight: 600, color: "#9ca3af", letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 5, display: "block" };
  const inp = (x = {}) => ({ padding: "8px 11px", borderRadius: 8, border: "1px solid rgba(0,0,0,0.08)", background: "#f9f8f7", fontSize: 12, color: "#111827", outline: "none", width: "100%", boxSizing: "border-box", ...x });

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }} onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: 18, boxShadow: "0 8px 48px rgba(0,0,0,0.2)", width: "100%", maxWidth: 520, maxHeight: "92vh", overflow: "auto" }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: "20px 24px", borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: "#111827", }}>
            {form?.type === "return" ? "Return to Account" : "Draw Petty Cash"}
          </h2>
          {initial && <p style={{ margin: "4px 0 0", fontSize: 12, color: "#d97706" }}>⚡ Suggested from Bills — confirm to add to Tracker</p>}
        </div>

        <div style={{ padding: "22px 24px" }}>
          {/* Type toggle */}
          {!initial && (
            <div style={{ display: "flex", gap: 0, borderRadius: 10, border: "1px solid rgba(0,0,0,0.08)", overflow: "hidden", marginBottom: 18 }}>
              {[
                { v: "draw", label: "💸 Draw Petty Cash" },
                { v: "return", label: "💰 Return to Account" }
              ].map(({ v, label }) => (
                <button key={v} onClick={() => setForm(v === "draw" ? { ...BLANK_DRAW } : { ...BLANK_RETURN })}
                  style={{
                    flex: 1, padding: "10px", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600,
                    background: form.type === v ? (v === "draw" ? "#f0fdf4" : "#fef2f2") : "#f9fafb",
                    color: form.type === v ? (v === "draw" ? "#16a34a" : "#dc2626") : "#6b7280", transition: "all 0.15s"
                  }}>
                  {label}
                </button>
              ))}
            </div>
          )}

          {form.type === "return" && (
            <div style={{ background: "#eff6ff", borderRadius: 10, border: "1px solid #bfdbfe", padding: "14px 16px", marginBottom: 12 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 3 }}>
                {[{ label: "PETTY CASH AVAILABLE", val: pkr(pettyCashBalance?.balance) }].map(r => (
                  <div key={r.label}>
                    <div className="text-sm font-bold text-[#2563eb] pb-1">
                      {r.label}
                    </div>
                    <div className="text-xs font-medium text-[#1d4ed8] tracking-wide">
                      {r.val}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {form.type === "draw" && (
            <div style={{ background: "#fffbeb", borderRadius: 10, border: "1px solid #fde68a", padding: "14px 16px", marginBottom: 12 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 3 }}>
                {[{
                  label: "CHEQUE-BASED WITHDRAWAL",
                  val: "Record petty cash withdrawals via cheque. Upload a scan of the cheque for records."
                }].map(r => (
                  <div key={r.label}>
                    <div className="text-sm font-bold text-[#d97706] pb-1">
                      {r.label}
                    </div>
                    <div className="text-xs font-medium text-[#b45309] tracking-wide">
                      {r.val}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <div>
              <label style={lbl}>Date</label>
              <input type="date" value={form.date} onChange={e => set("date", e.target.value)} style={inp()} />
            </div>
            {form.type === "draw" && (
              <div>
                <label style={lbl}>Cheque Number</label>
                <input value={form.cheque} onChange={e => set("cheque", e.target.value)} placeholder="e.g. REM-MAR-01" style={inp()} />
              </div>
            )}
            {form.type === "return" && (
              <div>
                <label style={lbl}>Ref. #</label>
                <input value={form.cheque} onChange={e => set("cheque", e.target.value)} placeholder="e.g. REM-MAR-01" style={inp()} />
              </div>
            )}
            <div>
              <label style={lbl}>Amount (PKR)</label>
              <input type="number" value={form.amount} onChange={e => set("amount", e.target.value)} placeholder="e.g. 15000" style={inp()} />
            </div>
            <div style={{ gridColumn: "1/-1" }}>
              <label style={lbl}>Notes</label>
              <input value={form.notes} onChange={e => set("notes", e.target.value)} placeholder="e.g. 15000" style={inp()} />
            </div>
          </div>

          {/* Attachment */}
          <div style={{ marginBottom: 20 }}>
            <label style={lbl}>Receipt / Invoice</label>
            <FileUploadProgressBar onChange={e => set("attachments", e)} />
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, paddingTop: 14, borderTop: "1px solid rgba(0,0,0,0.08)" }}>
            <button onClick={onClose} style={{ padding: "9px 20px", borderRadius: 9, border: "1px solid rgba(0,0,0,0.08)", background: "#f9f8f7", fontSize: 13, fontWeight: 600, color: "#6b7280", cursor: "pointer" }}>Cancel</button>
            <button onClick={() => onSave({ ...form, whtCalc: whtAmt, pkrNet })}
              style={{ padding: "9px 24px", borderRadius: 9, border: "none", background: form.type === "remittance" ? "#16a34a" : "#4f6ef7", fontSize: 13, fontWeight: 700, color: "#fff", cursor: "pointer", boxShadow: `0 2px 8px ${form.type === "remittance" ? "rgba(22,163,74,0.3)" : "rgba(79,110,247,0.3)"}` }}>
              {form.type === "draw" ? "✓ Record Withdrawal" : "↩ Return Cash"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN ─────────────────────────────────────────────────────────────────────
export default function TrackerAll() {
  const apiCall = useApiCall();
  const [entries, setEntries] = useState([]);
  const [pending, setPending] = useState(""); //INITIAL_PENDING
  const [showModal, setShowModal] = useState(false);
  const [confirmEntry, setConfirmEntry] = useState(null);
  const [showRecon, setShowRecon] = useState(false);
  const [filterMonth, setFilterMonth] = useState("All");
  const [filterType, setFilterType] = useState("All");
  const [filterLoc, setFilterLoc] = useState("All");
  const [filterCat, setFilterCat] = useState("All");
  const [search, setSearch] = useState("");
  const [expandedGroups, setExpandedGroups] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [summary, setSummary] = useState({ bank_balance: 0, total_in: 0, total_out: 0, petty_cash: 0, net_flow: 0 });
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [dropdowns, setDropdowns] = useState({ category: [], vendor: [], location: [], method: [] });
  const [fxRate, setFxRate] = useState(0);
  const [pettyCashBalance, setPettyCashBalance] = useState({});
  const [whtRate, setWhtRate] = useState(0);

  const fetchPettyCashBalance = async () => {
    try {
      const res = await apiCall({ method: 'get', url: "cash-flow/tracker/petty-cash/balance" })
      setPettyCashBalance(res?.data);
      console.log("res?.data", res)
    } catch (error) {
      console.log("Error: ", error)
    }
  }

  const fetchTrackerData = async () => {
    setIsLoading(true);
    try {
      const res = await apiCall({ url: "cash-flow/tracker/list", method: "GET" });
      const mappedData = res.data.map(item => ({
        id: item.id,
        date: item.date.split('T')[0], // Extract date part
        type: item.type,
        desc: item.description,
        cat: item.category_name || "",
        loc: item.location_name || "",
        amt: parseFloat(item.amount_pkr),
        usd: item.amount_usd ? parseFloat(item.amount_usd) : null,
        fx: item.fx_rate ? parseFloat(item.fx_rate) : null,
        wht: item.wht_amount ? parseFloat(item.wht_amount) : null,
        net: parseFloat(item.net_amount_pkr),
        method: item.payment_method,
        ref: item.reference,
        chequeGroup: item.cheque_group || null,
        month: item.bill_month,
        reconciled: item.reconciled,
        attachments: "", // Not provided in API
        balance: parseFloat(item.running_balance) // Use running_balance from API
      }));
      setEntries(mappedData);
      setSummary(res.summary);
      setIsLoading(false);
      fetchPettyCashBalance();
    } catch (error) {
      console.error("Error fetching tracker data:", error);
      setIsLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await apiCall({ url: "cash-flow/categories/list", method: "GET" });
      setCategories(res.data || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchLocations = async () => {
    try {
      const res = await apiCall({ url: "cash-flow/locations/list", method: "GET" });
      setLocations(res.data || []);
    } catch (error) {
      console.error("Error fetching locations:", error);
    }
  };

  const fetchDropdowns = async () => {
    try {
      const res = await apiCall({ url: "cash-flow/dropdown", method: "GET" });
      setDropdowns(res.dropdown || { category: [], vendor: [], location: [], method: [] });
    } catch (error) {
      console.error("Error fetching dropdowns:", error);
    }
  };

  const fetchFxRate = async () => {
    try {
      const res = await apiCall({ url: "cash-flow/fx-rates/latest", method: "GET" });
      setFxRate(parseFloat(res.data.rate) || 279.2);
    } catch (error) {
      console.error("Error fetching FX rate:", error);
      setFxRate(279.2); // fallback
    }
  };

  const fetchWHTRate = async () => {
    try {
      const res = await apiCall({ url: "cash-flow/wht-rate/detail", method: "GET" });
      setWhtRate(parseFloat(res.data.rate) || 0);
    } catch (error) {
      console.error("Error fetching FX rate:", error);
      setWhtRate(0); // fallback
    }
  };

  useEffect(() => {
    fetchTrackerData();
    fetchCategories();
    fetchLocations();
    fetchDropdowns();
    fetchFxRate();
    fetchWHTRate();
  }, []);

  const C = {
    navBg: "#1e2128", pageBg: "#EDE8E1", cardBg: "#ffffff",
    accent: "#4f6ef7", green: "#16a34a", red: "#dc2626", amber: "#d97706",
    textPri: "#111827", textSec: "#6b7280", textMut: "#9ca3af",
    border: "rgba(0,0,0,0.08)", shadow: "0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.08)",
  };
  const card = (x = {}) => ({ background: C.cardBg, borderRadius: 14, border: `1px solid ${C.border}`, boxShadow: C.shadow, ...x });

  // Use running balance from API (already calculated server-side)
  const withBalance = useMemo(() => entries.map(e => ({
    ...e,
    balance: e.balance,
    delta: e.type === "remittance" ? e.net : -e.amt
  })).sort((a, b) => new Date(b.date) - new Date(a.date)), [entries]);

  // Filtered
  const filtered = useMemo(() => withBalance.filter(e => {
    if (filterMonth !== "All" && e.month !== filterMonth) return false;
    if (filterType !== "All" && e.type !== filterType) return false;
    if (filterLoc !== "All" && e.loc !== filterLoc) return false;
    if (filterCat !== "All" && e.cat !== filterCat) return false;
    if (search && !e.desc.toLowerCase().includes(search.toLowerCase()) && !(e.ref || "").toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }), [withBalance, filterMonth, filterType, filterLoc, filterCat, search]);

  // Summary stats from API
  const totalIn = summary.total_in;
  const totalOut = summary.total_out;
  const curBal = summary.bank_balance;
  const pettyCash = summary?.petty_cash;

  // Save entry
  const saveEntry = async (form) => {
    try {
      if (form.type === "expense") {
        const category = categories.find(c => c.id === parseInt(form.cat));
        const location = locations.find(l => l.id === parseInt(form.loc));
        const payload = {
          description: form.desc,
          date: form.date,
          amount_pkr: parseFloat(form.amt),
          category_id: category ? category.id : null,
          location_id: location ? location.id : null,
          payment_method: form.from_petty_cash ? "Cash" : form.method,
          reference: form.ref || "",
          bill_month: form.month || "",
          bill_year: form.year || "",
          from_petty_cash: form.from_petty_cash,
          attachment: form.attachments || "",
        };
        await apiCall({ url: "cash-flow/tracker/expense", method: "POST", data: payload });
      } else if (form.type === "remittance") {
        const payload = {
          date: form.date,
          amount_usd: parseFloat(form.usd),
          fx_rate: parseFloat(form.fx),
          reference: form.ref || "",
          attachment: form.attachments || "",
        };
        await apiCall({ url: "cash-flow/tracker/remittance", method: "POST", data: payload });
      } else if (form?.type === 'draw') {
        const payload = {
          date: form.date,
          cheque_number: form.cheque,
          amount_pkr: parseFloat(form.amount),
          notes: form.notes,
          attachment: form.attachments || ""
        };
        await apiCall({ url: "cash-flow/tracker/petty-cash/draw", method: "POST", data: payload });
      } else if (form?.type === 'return') {
        const payload = {
          date: form.date,
          amount_pkr: parseFloat(form.amount),
          notes: form.notes,
          attachment: form.attachments || ""
        };
        await apiCall({ url: "cash-flow/tracker/petty-cash/return", method: "POST", data: payload });
      }


      // Refetch data after successful save
      await fetchTrackerData();
      setShowModal(false);
      setShowRecon(false)
      setConfirmEntry(null);
    } catch (error) {
      console.error("Error saving entry:", error);
      // Handle error, maybe show toast
    }
  };

  // Confirm pending suggestion
  const confirmPending = (p) => {
    const category = categories.find(c => c.name === p.cat);
    const location = locations.find(l => l.name === p.loc);
    setConfirmEntry({
      type: "expense", date: p.date, desc: p.expense, cat: category ? category.id : 1,
      loc: location ? location.id : "",
      amt: p.amt, method: p.method, ref: p.ref,
      chequeGroup: p.ref || "", month: p.month,
      attachments: "",
    });
    setPending(prev => prev.filter(x => x.id !== p.id));
  };
  const dismissPending = (id) => setPending(p => p.filter(x => x.id !== id));

  // Toggle cheque group expand
  const toggleGroup = (ref) => setExpandedGroups(p => ({ ...p, [ref]: !p[ref] }));

  return (
    isLoading ? (
      <div className="flex items-center justify-center h-full w-full">
        <InnerLoader />
      </div>
    ) : (
      <div className="overflow-auto flex flex-col">
        <div style={{ flex: 1, padding: "24px 0px", width: "100%", margin: "0 auto" }}>

          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
            <div>
              <h1 style={{ fontSize: 22, fontWeight: 800, color: C.textPri, letterSpacing: -0.5, margin: 0 }}>Ledger</h1>
              <p style={{ fontSize: 13, color: C.textSec, marginTop: 3 }}>Full ledger · every transaction · running PKR balance</p>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setShowRecon(true)} style={{ display: "flex", alignItems: "center", gap: 7, padding: "8px 18px", borderRadius: 10, background: "#fff", border: `1px solid ${C.border}`, fontSize: 13, fontWeight: 600, color: C.textSec, cursor: "pointer", boxShadow: C.shadow }}>📄 Petty Cash</button>
              <button onClick={() => setShowModal(true)} style={{ display: "flex", alignItems: "center", gap: 7, padding: "8px 18px", borderRadius: 10, background: C.accent, border: "none", fontSize: 13, fontWeight: 600, color: "#fff", cursor: "pointer", boxShadow: "0 2px 8px rgba(79,110,247,0.3)" }}>+ Add Entry</button>
            </div>
          </div>

          {/* KPI CARDS */}
          {/* <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14, marginBottom: 18 }}>
            {[
              {
                label: "Total In",
                val: pkr(totalIn),
                sub: "All remittances",
                c: C.green,
                bg: "#f0fdf4",
                bd: "#bbf7d0",
                icon: <ArrowDownCircle size={18} />
              },
              {
                label: "Total Out",
                val: pkr(totalOut),
                sub: "All expenses",
                c: C.red,
                bg: "#fef2f2",
                bd: "#fecaca",
                icon: <ArrowUpCircle size={18} />
              },
              {
                label: "Petty Cash",
                val: pkr(pettyCash),
                sub: "Running PKR",
                c: C.accent,
                bg: "#f5f7ff",
                bd: "#c7d2fe",
                icon: <Wallet size={18} />
              },
              {
                label: "Net Flow",
                val: pkr(totalIn - totalOut),
                sub: totalIn - totalOut >= 0 ? "Surplus" : "Deficit",
                c: totalIn - totalOut >= 0 ? C.green : C.red,
                bg: totalIn - totalOut >= 0 ? "#f0fdf4" : "#fef2f2",
                bd: totalIn - totalOut >= 0 ? "#bbf7d0" : "#fecaca",
                icon: <Scale size={18} />
              }
            ].map((k, i) => (
              <MetricCard
                icon={<span>{k.icon}</span>}
                iconColor={k.c}
                valueColor={k.c}
                badge={{ text: 'MTD', type: 'neutral' }}
                value={k.val}
                statsBadge={k.sub}
                label={k.label}
                bg={k.bg}
              />
              // <div key={i} style={{ ...card({ padding: "16px 18px", background: k.bg, border: `1px solid ${k.bd}` }) }}>
              //   <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
              //     <span style={{ fontSize: 10, fontWeight: 700, color: k.c, letterSpacing: 0.7, textTransform: "uppercase" }}>{k.label}</span>
              //     <span style={{ fontSize: 18 }}>{k.icon}</span>
              //   </div>
              //   <div style={{ fontSize: 20, fontWeight: 800, color: k.c, letterSpacing: -0.3, marginBottom: 3 }}>{k.val}</div>
              //   <div style={{ fontSize: 11, color: k.c, opacity: 0.7 }}>{k.sub}</div>
              // </div>
            ))}
          </div> */}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14, marginBottom: 18 }}>
            {[
              {
                label: "Total In",
                val: pkr(totalIn),
                sub: "All remittances",
                c: C.green,
                bg: "#f0fdf4",
                bd: "#bbf7d0",
                icon: <ArrowDownCircle size={18} />
              },
              {
                label: "Total Out",
                val: pkr(totalOut),
                sub: "All expenses",
                c: C.red,
                bg: "#fef2f2",
                bd: "#fecaca",
                icon: <ArrowUpCircle size={18} />
              },
              {
                label: "Petty Cash",
                val: pkr(pettyCash),
                sub: "Cash in hand",
                c: C.accent,
                bg: "#f5f7ff",
                bd: "#c7d2fe",
                icon: <Wallet size={18} />
              },
              {
                label: "Net Flow",
                val: pkr(totalIn - totalOut),
                sub: totalIn - totalOut >= 0 ? "Surplus" : "Deficit",
                c: totalIn - totalOut >= 0 ? C.green : C.red,
                bg: totalIn - totalOut >= 0 ? "#f0fdf4" : "#fef2f2",
                bd: totalIn - totalOut >= 0 ? "#bbf7d0" : "#fecaca",
                icon: <Scale size={18} />
              }
            ].map((k, i) => (
              <MetricCard
                icon={<span>{k.icon}</span>}
                iconColor={k.c}
                iconBg={k.bg}
                valueColor={k.c}
                badge={{ text: 'MTD', type: 'neutral' }}
                value={k.val}
                statsBadge={k.sub}
                label={k.label}
                bg={null}
              />
            ))}
          </div>

          {/* PENDING SUGGESTIONS */}
          {pending.length > 0 && (
            <div className="!hidden" style={{ ...card({ padding: "16px 20px", marginBottom: 16, background: "#fffbeb", border: "1px solid #fde68a" }) }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <span style={{ fontSize: 18 }}>⚡</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: C.amber }}>Pending Suggestions from Bills</span>
                <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: "#fef9c3", border: "1px solid #fde68a", color: C.amber, fontWeight: 700 }}>{pending.length}</span>
                <span style={{ fontSize: 11, color: C.textMut, marginLeft: 4 }}>These bills were marked paid — confirm to add to Tracker</span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {pending.map(p => (
                  <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", borderRadius: 10, background: "#fff", border: "1px solid #fde68a" }}>
                    <span style={{ fontSize: 15, flexShrink: 0 }}>{categories.find(c => c.name === p.cat)?.icon || "💳"}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 600, color: C.textPri, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.expense}</div>
                      <div style={{ fontSize: 10, color: C.textMut, marginTop: 1, display: "flex", gap: 6, alignItems: "center" }}>
                        {p.loc && <span style={{ padding: "1px 6px", borderRadius: 20, background: `${locations.find(l => l.name === p.loc)?.color || "#9ca3af"}18`, color: locations.find(l => l.name === p.loc)?.color || "#9ca3af", fontSize: 9, fontWeight: 700 }}>{p.loc}</span>}
                        <span>{p.vendor}</span>
                        <span>· {p.date}</span>
                        {p.ref && <span>· Chq: {p.ref}</span>}
                      </div>
                    </div>
                    <div style={{ textAlign: "right", marginRight: 10, flexShrink: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 800, color: C.textPri }}>{pkr(p.amt)}</div>
                    </div>
                    <button onClick={() => confirmPending(p)} style={{ padding: "5px 14px", borderRadius: 8, border: "none", background: C.accent, fontSize: 11, fontWeight: 700, color: "#fff", cursor: "pointer", whiteSpace: "nowrap" }}>Confirm ✓</button>
                    <button onClick={() => dismissPending(p.id)} style={{ padding: "5px 10px", borderRadius: 8, border: `1px solid ${C.border}`, background: "#fff", fontSize: 11, color: C.textMut, cursor: "pointer" }}>✕</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* FILTER BAR */}
          <div style={{ ...card({ padding: "11px 16px", marginBottom: 14, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }) }}>
            <div style={{ position: "relative", flex: "1 1 160px" }}>
              <span style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", fontSize: 12, color: C.textMut }}>🔍</span>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search description or reference…"
                style={{ padding: "6px 10px 6px 29px", borderRadius: 8, border: `1px solid ${C.border}`, background: "#f9f8f7", fontSize: 12, color: C.textPri, outline: "none", width: "100%", boxSizing: "border-box" }} />
            </div>
            {/* Type pills */}
            <div style={{ display: "flex", gap: 3 }}>
              {[{ v: "All", label: "All" }, { v: "remittance", label: "💵 Remittances" }, { v: "expense", label: "📤 Expenses" }].map(({ v, label }) => (
                <button key={v} onClick={() => setFilterType(v)} style={{ padding: "5px 11px", borderRadius: 20, border: "none", cursor: "pointer", fontSize: 11, fontWeight: 600, background: filterType === v ? C.accent : "#f3f4f6", color: filterType === v ? "#fff" : C.textSec, transition: "all 0.15s", whiteSpace: "nowrap" }}>{label}</button>
              ))}
            </div>
            <div style={{ width: 1, height: 18, background: C.border }} />
            {[
              { val: filterMonth, set: setFilterMonth, opts: ["All Months", ...MONTHS] },
              { val: filterLoc, set: setFilterLoc, opts: ["All Offices", ...locations.map(l => l.name)] },
              { val: filterCat, set: setFilterCat, opts: ["All Categories", ...categories.map(c => c.name), "Remittance"] },
            ].map((f, i) => (
              <select key={i} value={f.val} onChange={e => f.set(e.target.value.startsWith("All") ? "All" : e.target.value)}
                style={{ padding: "6px 10px", borderRadius: 8, border: `1px solid ${C.border}`, background: "#fff", fontSize: 11, color: C.textPri, cursor: "pointer", outline: "none" }}>
                {f.opts.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            ))}
            {(search || filterType !== "All" || filterMonth !== "All" || filterLoc !== "All" || filterCat !== "All") &&
              <button onClick={() => { setSearch(""); setFilterType("All"); setFilterMonth("All"); setFilterLoc("All"); setFilterCat("All"); }}
                style={{ padding: "5px 10px", borderRadius: 8, border: `1px solid ${C.border}`, background: "#fff", fontSize: 11, color: C.textSec, cursor: "pointer" }}>✕ Clear</button>
            }
            <span style={{ marginLeft: "auto", fontSize: 11, color: C.textMut }}>{filtered.length} of {entries.length} entries</span>
          </div>

          {/* LEDGER TABLE */}
          <div style={card({ padding: 0, overflow: "hidden" })}>
            {/* Column headers */}
            <div style={{ display: "grid", gridTemplateColumns: "100px 35px 2fr 100px 110px 120px 180px 120px 150px", borderLeft: '3px solid transparent', gap: 0, background: "#f9f8f7", borderBottom: `1px solid ${C.border}`, padding: "0 16px" }}>
              {["Date", "", "Description", "Month", "Office", "Category", "Amount", "Method", "Balance"].map((h, i) => (
                <div
                  key={i}
                  style={{
                    padding: "10px 10px", fontSize: 14,
                    fontWeight: 600, color: "#000",
                    textTransform: "capitalize",
                    borderRight: i < 8 ? `1px solid ${C.border}` : "none"
                  }}
                >
                  {h}
                </div>
              ))}
            </div>

            {filtered.length === 0 && (
              <div style={{ padding: "48px", textAlign: "center" }}>
                <DataNotFound title={'Tracker'} />
              </div>
            )}

            {filtered.map((e, i) => {
              const isRem = e.type === "remittance";
              const locColor = locations.find(l => l.name === e.loc)?.color || "#9ca3af";
              const isGrouped = e.chequeGroup;
              const groupExpanded = expandedGroups[e.chequeGroup];

              return (
                <div key={e.id}>
                  <div style={{
                    display: "grid", gridTemplateColumns: "100px 35px 2fr 100px 110px 120px 180px 120px 150px",
                    gap: 0, padding: "0 16px",
                    background: isRem ? "#f6fef9" : i % 2 === 0 ? "#fff" : "#fafaf9",
                    borderBottom: `1px solid ${C.border}`,
                    borderLeft: `3px solid ${isRem ? "#86efac" : isGrouped ? "#c4b5fd" : e.reconciled ? "#d1d5db" : "transparent"}`,
                  }}>
                    {/* Date */}
                    <div style={{ padding: "12px 10px", borderRight: `1px solid ${C.border}`, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                      <div style={{ fontSize: 11.5, fontWeight: 600, color: C.textPri }}>{dateLabel(e.date)}</div>
                      {e.reconciled && <div style={{ fontSize: 9, color: C.textMut, marginTop: 2 }}>✓ Reconciled</div>}
                    </div>
                    {/* Icon */}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", borderRight: `1px solid ${C.border}`, fontSize: 14 }}>
                      {isRem ? "💵" : categories.find(c => c.name === e.cat)?.icon || "💳"}
                    </div>
                    {/* Description */}
                    <div style={{ padding: "12px 12px", borderRight: `1px solid ${C.border}`, display: "flex", flexDirection: "column", justifyContent: "center", minWidth: 0 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 600, color: C.textPri, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.desc}</div>
                      <div style={{ display: "flex", gap: 6, marginTop: 2, alignItems: "center", flexWrap: "wrap" }}>
                        {isRem && e.usd && (
                          <>
                            <span style={{ fontSize: 10, fontWeight: 600, color: "#16a34a" }}>{usd(e.usd)}</span>
                            <span style={{ fontSize: 10, color: C.textMut }}>@ ₨{e.fx}</span>
                            <span style={{ fontSize: 10, color: "#d97706" }}>WHT: {pkr(e.wht)}</span>
                          </>
                        )}
                        {!isRem && e.ref && <span style={{ fontSize: 10, color: C.textMut }}>Chq: {e.ref}</span>}
                        {isGrouped && (
                          <button onClick={() => toggleGroup(e.chequeGroup)}
                            style={{ display: "inline-flex", alignItems: "center", gap: 3, padding: "1px 7px", borderRadius: 20, background: "#ede9fe", border: "1px solid #c4b5fd", fontSize: 9, fontWeight: 700, color: "#7c3aed", cursor: "pointer" }}>
                            📝 Combined {e.chequeGroup} {groupExpanded ? "▲" : "▼"}
                          </button>
                        )}
                        {/* {e.attachments && <span style={{ fontSize: 10, color: C.textMut }}>📎 {e.attachments.length}</span>} */}
                      </div>
                    </div>
                    {/* Month */}
                    <div style={{ padding: "12px 10px", borderRight: `1px solid ${C.border}`, display: "flex", alignItems: "center" }}>
                      <span style={{ fontSize: 11, color: C.textSec }}>{e.month}</span>
                    </div>
                    {/* Location */}
                    <div style={{ padding: "12px 10px", borderRight: `1px solid ${C.border}`, display: "flex", alignItems: "center" }}>
                      {e.loc
                        ? <span style={{ display: "inline-flex", alignItems: "center", gap: 3, padding: "2px 8px", borderRadius: 20, background: `${locColor}15`, border: `1px solid ${locColor}30`, fontSize: 10, fontWeight: 700, color: locColor }}>{e.loc}</span>
                        : <span style={{ fontSize: 10, color: C.textMut, fontStyle: "italic" }}>General</span>
                      }
                    </div>
                    {/* Category */}
                    <div style={{ padding: "12px 10px", borderRight: `1px solid ${C.border}`, display: "flex", alignItems: "center" }}>
                      <span style={{ fontSize: 11, color: C.textSec }}>{e.cat}</span>
                    </div>
                    {/* Amount */}
                    <div style={{ padding: "12px 10px", borderRight: `1px solid ${C.border}`, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "flex-end" }}>
                      <span style={{ fontSize: 13, fontWeight: 800, color: isRem ? C.green : C.red, }}>
                        {isRem ? "+" : "-"}{pkr(isRem ? e.net : e.amt)}
                      </span>
                      {isRem && e.usd && <span style={{ fontSize: 10, color: C.textMut, marginTop: 1 }}>{usd(e.usd)}</span>}
                    </div>
                    {/* Method */}
                    <div style={{ padding: "12px 10px", borderRight: `1px solid ${C.border}`, display: "flex", alignItems: "center" }}>
                      <span style={{ fontSize: 11, color: C.textSec }}>
                        {e.method === "Cheque" ? "📝" : e.method === "Cash" ? "💵" : e.method === "Bank Transfer" ? "🏦" : "💻"}
                        {" "}{e.method}
                      </span>
                    </div>
                    {/* Running Balance */}
                    <div style={{ padding: "12px 10px", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "flex-end" }}>
                      <span style={{ fontSize: 13, fontWeight: 800, color: e.balance < 50000 ? C.amber : C.accent, }}>{pkr(e.balance)}</span>
                      {e.balance < 50000 && <span style={{ fontSize: 9, color: C.amber, fontWeight: 700, marginTop: 1 }}>⚠ LOW</span>}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Footer totals */}
            {filtered.length > 0 && (
              <div style={{ display: "grid", gridTemplateColumns: "100px 35px 2fr 100px 110px 120px 180px 120px 150px", gap: 0, background: "#f3f4f6", borderTop: `2px solid ${C.border}`, padding: "0 16px" }}>
                <div style={{ padding: "11px 10px", gridColumn: "1/7", fontSize: 11, fontWeight: 700, color: C.textSec }}>{filtered.length} entries shown</div>
                <div style={{ padding: "11px 10px", display: "flex", flexDirection: "column", alignItems: "flex-end", justifyContent: "center" }}>
                  <div style={{ fontSize: 11, color: C.green, fontWeight: 700 }}>+{pkr(filtered.filter(e => e.type === "remittance").reduce((s, e) => s + e.net, 0))}</div>
                  <div style={{ fontSize: 11, color: C.red, fontWeight: 700 }}>-{pkr(filtered.filter(e => e.type === "expense").reduce((s, e) => s + e.amt, 0))}</div>
                </div>
                <div style={{ padding: "11px 10px" }} />
                <div style={{ padding: "11px 10px", display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
                  <span style={{ fontSize: 13, fontWeight: 800, color: C.accent, }}>{pkr(curBal)}</span>
                </div>
              </div>
            )}
          </div>
        </div>


        {/* MODALS */}
        {(showModal || confirmEntry) &&
          <AddModal
            initial={confirmEntry}
            onSave={saveEntry}
            onClose={() => { setShowModal(false); setConfirmEntry(null); }}
            C={C}
            categories={categories} 
            locations={locations} 
            dropdowns={dropdowns} 
            fxRate={fxRate}
            whtRate={whtRate}
          />
        }

        {showRecon &&
          <AddPettyCashModal
            pettyCashBalance={pettyCashBalance}
            onClose={() => { setShowRecon(false) }}
            onSave={saveEntry}
          />
          // <ReconPanel entries={entries} onClose={() => setShowRecon(false)} C={C} />
        }
      </div>
    )
  );
}